import { CLASS_NAMES, SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the List and Image Caption Toggle
 */
export default class ListAndImage extends MessageBus {
  /**
   * Constructor function of the ListAndImage class. Must receive one argument, a DOM
   * node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {ListAndImage} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {ListAndImage} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.captionToggle = this.element.querySelector(
      `.${SELECTORS.LIST_AND_IMAGE_CAPTION_TOGGLE}`
    );
    this.caption = this.element.querySelector(
      `.${SELECTORS.LIST_AND_IMAGE_CAPTION}`
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {ListAndImage} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleCaptionToggle = this.onCaptionToggle.bind(this);
    this.handleOutsideClick = this.onOutsideClick.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with view
   * @returns {ListAndImage} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.captionToggle) {
      this.captionToggle.addEventListener(
        EVENTS.CLICK,
        this.handleCaptionToggle
      );
    }

    return this;
  }

  /**
   * Handles clicks outside the caption — closes if expanded.
   * @param {MouseEvent} e A reference to the click event object
   * @returns {ListAndImage} A reference to the current instance of the class
   */
  onOutsideClick(e) {
    if (!this.caption.contains(e.target)) {
      this.closeCaption();
    }

    return this;
  }

  /**
   * Handles caption toggle click — animates open or close.
   * @param {MouseEvent} e A reference to the click event object
   * @returns {ListAndImage} A reference to the current instance of the class
   */
  onCaptionToggle(e) {
    e.stopPropagation();

    const isExpanded = this.caption.classList.contains(CLASS_NAMES.IS_EXPANDED);

    if (!isExpanded) {
      this.openCaption();
    } else {
      this.closeCaption();
    }

    return this;
  }

  /**
   * Measures natural dimensions and animates the caption open.
   * @returns {ListAndImage} A reference to the current instance of the class
   */
  openCaption() {
    this.caption.style.transition = 'none';
    this.caption.style.width = '281px';
    this.caption.style.height = 'auto';

    const naturalHeight = this.caption.scrollHeight;

    this.caption.style.width = '44px';
    this.caption.style.height = '44px';

    // Force reflow
    this.caption.getBoundingClientRect();

    this.caption.style.transition = '';
    this.caption.style.width = '281px';
    this.caption.style.height = `${naturalHeight}px`;
    this.caption.classList.add(CLASS_NAMES.IS_EXPANDED);
    this.caption.setAttribute(SELECTORS.ARIA_EXPANDED, 'true');

    document.addEventListener(EVENTS.CLICK, this.handleOutsideClick);

    return this;
  }

  /**
   * Pins current dimensions and animates the caption closed.
   * @returns {ListAndImage} A reference to the current instance of the class
   */
  closeCaption() {
    if (!this.caption.classList.contains(CLASS_NAMES.IS_EXPANDED)) return this;

    document.removeEventListener(EVENTS.CLICK, this.handleOutsideClick);

    this.caption.style.width = `${this.caption.scrollWidth}px`;
    this.caption.style.height = `${this.caption.scrollHeight}px`;

    // Force reflow
    this.caption.getBoundingClientRect();

    this.caption.style.width = '44px';
    this.caption.style.height = '44px';

    const onTransitionEnd = (e) => {
      if (e.propertyName !== 'width') return;
      this.caption.classList.remove(CLASS_NAMES.IS_EXPANDED);
      this.caption.setAttribute(SELECTORS.ARIA_EXPANDED, 'false');
      this.caption.removeEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);
    };

    this.caption.addEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);

    return this;
  }
}
